import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";

import { NO_PHASE } from "fond/constants";
import { FullReport, OkResponse, Report, ReportConfiguration as TReportConfiguration, SchedulesDataItem, SomeRequired, Store } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { searchCollectionByKey } from "fond/utils/search";

import { selectCurrentAccount } from "./accountSlice";
import { apiSlice } from "./apiSlice";

export type GetReportsResponse = {
  Reports: Report[];
};

export const reportsAdapter = createEntityAdapter<Report>({
  selectId: (entity: Report): string => entity.ID,
});
const reportsInitialState = reportsAdapter.getInitialState();

/**
 * Transform the get report response:
 * 1. Filter out the "All" or "Horizon" or both Phases that we don't know how to process yet.
 * 2. Add "NO_PHASE" to the end of the BuildOrderMap.Phases if CoveragePassings is not 1(100%).
 */
export const transformGetReportResponse = (response: FullReport): FullReport => {
  const { Schedules, CoveragePassings, BuildOrderMap, ...rest } = response;
  const excludePhase = (schedule: SchedulesDataItem) => !["Horizon", "All"].includes(schedule.Phase);
  const excludeAllPhase = (schedule: SchedulesDataItem) => schedule.Phase !== "All";
  const report: FullReport = {
    ...rest,
    CoveragePassings,
    Schedules: Schedules
      ? {
          Hhp: Schedules.Hhp.filter(excludePhase),
          Hhc: Schedules.Hhc.filter(excludePhase),
          Revenue: Schedules.Revenue.filter(excludeAllPhase),
          Cost: Schedules.Cost.filter(excludePhase),
          Cashflow: Schedules.Cashflow.filter(excludeAllPhase),
        }
      : Schedules,
    BuildOrderMap: {
      ...BuildOrderMap,
      Phases: CoveragePassings !== 1 && BuildOrderMap?.Phases ? [...BuildOrderMap.Phases, NO_PHASE] : BuildOrderMap?.Phases,
    },
  };
  return report;
};

/**
 * Reports API Slice
 */
export const reportSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getReport: build.query<FullReport, string>({
      query: (reportId) => `/v2/reports/${reportId}`,
      providesTags: (result) => (result ? [{ type: "Report", id: result.ID }] : []),
      transformResponse: transformGetReportResponse,
    }),
    getReports: build.query<ReturnType<typeof reportsAdapter.setAll>, { accountId?: string; multiReportId?: string } | void>({
      query: (params) => ({
        url: "/v2/reports",
        params: {
          account_id: params?.accountId,
          multi_report_id: params?.multiReportId,
        },
      }),
      transformResponse: (response: GetReportsResponse) => reportsAdapter.setAll(reportsInitialState, response.Reports),
      providesTags: (result, _error, params) => {
        const accountListId = `LIST:${params?.accountId || "all"}`;
        const listTags = [
          { type: "Report" as const, id: "LIST" },
          { type: "Report" as const, id: accountListId },
        ];
        return result ? [...result.ids.map((id) => ({ type: "Report" as const, id })), ...listTags] : listTags;
      },
    }),
    getReportsByMultiReport: build.query({
      query: (multiReportId: string) => ({
        url: "/v2/reports",
        params: { multi_report_id: multiReportId },
      }),
      transformResponse: (response: GetReportsResponse) => response?.Reports || [],
      providesTags: (result) =>
        result
          ? [...result.map((report) => ({ type: "Report" as const, id: report.id })), { type: "Report", id: "LIST" }]
          : [{ type: "Report", id: "LIST" }],
    }),
    createReport: build.mutation<
      Report,
      {
        Name: string;
        Description: string;
        FolderID: string | null;
        VersionID: string;
        ReportConfiguration: Partial<TReportConfiguration>;
      }
    >({
      query: ({ Name, Description, FolderID, VersionID, ReportConfiguration }) => ({
        url: "/v2/reports",
        method: "POST",
        body: {
          Name,
          Description,
          FolderID,
          VersionID,
          ReportConfiguration,
        },
      }),
      invalidatesTags: (result, _error, _arg) => (result ? [{ type: "Report", id: `LIST:${result.Account.ID}` }] : []),
    }),
    createServicesReport: build.mutation<
      Report,
      {
        Name: string;
        Description: string;
        FolderID: string | null;
        VersionID: string;
      }
    >({
      query: (args) => ({
        url: "/v2/reports",
        method: "POST",
        body: {
          ...args,
          Type: "financial_analytics_imported",
        },
      }),
      invalidatesTags: (result, _error, _arg) => (result ? [{ type: "Report", id: `LIST:${result.Account.ID}` }] : []),
    }),
    generateReport: build.mutation<FullReport, string>({
      query: (reportId) => ({
        url: `/v2/reports/${reportId}/generate`,
        method: "POST",
      }),
      onQueryStarted: async (reportId, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(reportSlice.util.upsertQueryData("getReport", reportId, data));
        } catch {
          // Do nothing
        }
      },
    }),
    importReport: build.mutation<FullReport, { reportId: string; formData: FormData }>({
      query: ({ reportId, formData }) => ({
        url: `/v2/reports/${reportId}/import`,
        method: "POST",
        body: formData,
        formData: true,
      }),
      onQueryStarted: async ({ reportId }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(reportSlice.util.upsertQueryData("getReport", reportId, data));
        } catch {
          // Do nothing
        }
      },
    }),
    updateReport: build.mutation<Report, SomeRequired<FullReport, "ID">>({
      query: ({ ID, ...reportData }) => ({
        url: `/v2/reports/${ID}`,
        method: "PATCH",
        body: reportData,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "Report", id: arg.ID }],
      onQueryStarted: async ({ ID, ...reportData }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          reportSlice.util.updateQueryData("getReports", ID, (draft) => {
            Object.assign(draft, reportData);
          })
        );
        const reportsPatchResult = dispatch(
          reportSlice.util.updateQueryData("getReports", undefined, (reports) => {
            const reportToUpdate = reports.entities[ID];
            if (reportToUpdate) {
              reportsAdapter.updateOne(reports, { id: ID, changes: reportData });
            }
          })
        );
        queryFulfilled
          .then(({ data }) => {
            dispatch(
              reportSlice.util.updateQueryData("getReport", ID, (draft: FullReport) => {
                Object.assign(draft, data);
              })
            );
          })
          .catch(() => {
            patchResult.undo();
            reportsPatchResult.undo();
          });
      },
    }),

    deleteReport: build.mutation<OkResponse, SomeRequired<Report, "ID">>({
      query: ({ ID, ...report }) => ({
        url: `/v2/reports/${ID}`,
        method: "DELETE",
        body: report,
      }),
      invalidatesTags: (_result, _error, arg) => [
        ...(arg.Account ? [{ type: "Report" as const, id: `LIST:${arg.Account.ID}` }] : []),
        { type: "Report", id: arg.ID },
      ],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useCreateReportMutation,
  useCreateServicesReportMutation,
  useGenerateReportMutation,
  useGetReportsQuery,
  useGetReportsByMultiReportQuery,
  useLazyGetReportsQuery,
  useGetReportQuery,
  useLazyGetReportQuery,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useImportReportMutation,
} = reportSlice;

/**
 * Custom hook to fetch reports for the current account from the URL.
 * This hook automatically passes the account ID from the URL to the query.
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useGetAccountReports();
 * ```
 */
export const useGetAccountReports = (): ReturnType<typeof useGetReportsQuery> => {
  const currentAccount = useAppSelector(selectCurrentAccount);
  const accountId = currentAccount?.ID;
  return useGetReportsQuery(accountId ? { accountId } : undefined, { skip: !accountId });
};

/**
 * Selectors
 */
const selectReportsAccountId = (state: Store): string | undefined => {
  const currentAccount = selectCurrentAccount(state);
  return currentAccount?.ID;
};

const selectReportsResult = createSelector([(state: Store) => state, selectReportsAccountId], (state, accountId) => {
  return reportSlice.endpoints.getReports.select(accountId ? { accountId } : undefined)(state);
});
export const selectReportsData = createSelector([selectReportsResult], (result) => result?.data ?? reportsInitialState);

export const { selectAll: selectAllReports, selectById: selectReportById } = reportsAdapter.getSelectors(selectReportsData);

/**
 * Returns all reports with the specified ParentID
 */
export const selectReportsByParentId = createSelector(
  [selectAllReports, (state: Store, parentId: string | null | undefined) => parentId],
  (reports, parentId): Report[] => {
    return reports.filter((report) => (parentId === undefined && !report.Folder?.ID) || report.Folder?.ID === parentId);
  }
);

export const selectReportsMatchingSearchKey = createSelector(
  [selectAllReports, (_: Store, searchKey: string) => searchKey],
  (reports, searchKey): Report[] => {
    return searchCollectionByKey(reports, searchKey, "Name");
  }
);

export const selectReportsByVersionId = createSelector(
  [selectAllReports, (_: Store, versionId: string | null | undefined) => versionId],
  (reports, versionId): Report[] => {
    return reports.filter((report) => report.Version?.ID === versionId);
  }
);
